import { useEffect, useState } from "react";
import HomeSkeleton from "../components/HomeSkeleton";
import { gravarContexto } from "../utils/contextoOrcamento";

// 🔐 DECODIFICA JWT (fora do componente para poder ser usado já no estado inicial)
const decodeJWT = (token) => {
  try {
    const cleanToken = token.replace("Bearer ", "");
    const parts = cleanToken.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload);
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error("Erro ao decodificar JWT:", error);
    return null;
  }
};

// 🔐 PERMISSÕES DIRETO DO TOKEN (sincrono, evita reflow no 1º render)
const readPermissionsFromToken = () => {
  const tokenMenu = localStorage.getItem("tokenMenu");
  if (!tokenMenu) return {};
  return decodeJWT(tokenMenu)?.permissions || {};
};

const HomePage = () => {
  // ⏰ ESTADOS BÁSICOS
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredCard, setHoveredCard] = useState(null);
  const [userName, setuserName] = useState(null);

  // 🔌 ESTADOS DA API ECONÔMICA
  const [economicData, setEconomicData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState("checking");

  // 🎛️ ESTADOS DO CONTEXTO
  const [showContextModal, setShowContextModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    localStorage.getItem("selectedYear") || new Date().getFullYear().toString()
  );
  const [selectedFilial, setSelectedFilial] = useState("");
  const [availableFiliais, setAvailableFiliais] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextInitialized, setContextInitialized] = useState(false);

  // 👤 ESTADOS DO USUÁRIO
  const [userProfile, setUserProfile] = useState({
    nome: "João",
    email: "",
    imagem: "",
    coligada: "",
    filial: "",
    codusuario: "",
  });
  const [profileLoading, setProfileLoading] = useState(true);

  // 📊 ESTADOS DO DASHBOARD
  const [dashboardData, setDashboardData] = useState({
    orcamento: 0,
    realizado: 0,
    economia: 0,
    risco: 0,
    performance: 0,
    previsao: 0,
  });
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);

  // � ESTADOS DO DASHBOARD RH
  const [dashboardDataRH, setDashboardDataRH] = useState({
    orcamento: 0,
    realizado: 0,
  });
  const [dashboardRHLoading, setDashboardRHLoading] = useState(true);

  // �️ ESTADOS DA IMAGEM - SIMPLIFICADOS
  const [avatarUrl] = useState(null);
  const [, setImageError] = useState(false);
  const [finalImageSrc, setFinalImageSrc] = useState(null);

  // 🔐 ESTADO DE PERMISSÕES (lido do token já no 1º render)
  const [permissions] = useState(readPermissionsFromToken);

  // 💀 CONTROLE DO SKELETON: true até o 1º carregamento terminar (nunca volta)
  const [booted, setBooted] = useState(false);

  // 🔧 CONFIGURAÇÃO DA API
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // 🔐 FUNÇÃO HELPER PARA VERIFICAR PERMISSÃO (do state)
  const hasPermission = (permission) => {
    return permissions[permission] === true;
  };

  // 🔐 FUNÇÃO PARA OBTER PERMISSÃO DIRETAMENTE DO TOKEN (útil antes do state carregar)
  const getPermissionFromToken = (permission) => {
    return readPermissionsFromToken()[permission] === true;
  };

  // ⏰ TIMER DO RELÓGIO
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 🎯 FUNÇÃO PARA INICIALIZAR CONTEXTO APENAS UMA VEZ - CORRIGIDA
  const initializeContextFromStorage = () => {
    console.log("🚀 Inicializando contexto do localStorage");

    const savedYear = localStorage.getItem("selectedYear");
    const savedFilial = localStorage.getItem("selectedFilial");

    console.log("📋 Contexto encontrado:", { savedYear, savedFilial });

    if (savedYear) {
      console.log("✅ Aplicando ano salvo:", savedYear);
      setSelectedYear(savedYear);
    }

    if (savedFilial) {
      console.log("✅ Aplicando filial salva:", savedFilial);
      setSelectedFilial(savedFilial);
    }

    setContextInitialized(true);
    return { savedYear, savedFilial };
  };

  // 📊 FUNÇÃO PARA BUSCAR DADOS DO DASHBOARD
  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);
      setDashboardError(null);

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Token não encontrado");
      }

      const contextYear =
        selectedYear ||
        localStorage.getItem("selectedYear") ||
        new Date().getFullYear().toString();
      const contextFilialChave =
        selectedFilial || localStorage.getItem("selectedFilial");

      console.log("📊 Carregando dashboard para:", {
        contextYear,
        contextFilialChave,
      });

      if (!contextFilialChave || !contextFilialChave.includes("-")) {
        console.warn(
          "🎯 Chave de filial inválida ou ausente:",
          contextFilialChave
        );
        setDashboardData({
          orcamento: 0,
          realizado: 0,
          economia: 0,
          risco: 0,
          performance: 0,
          previsao: 0,
        });
        return;
      }

      const [contextColigada, contextFilial] = contextFilialChave.split("-");
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0004/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            PARAM: [
              {
                CODFILIAL: contextFilial,
                CODCOLIGADA: contextColigada,
                ANO: contextYear,
              },
            ],
            WHERE: `AND O.CODTIPO IN (1,2,5) 
                    AND O.ANO = ${contextYear}
                    AND U.CODUSUARIO = '${currentUsername}' 
                    AND O.CODFILIAL = '${contextFilial}'
                    AND U.CODCOLIGADA = '${contextColigada}'`,
          }),
        }
      );

      const result = await response.json();

      if (result.success && result.data && result.data.length > 0) {
        const userData = result.data[0];
        console.log("✅ Dashboard carregado com sucesso para", contextYear);
        setDashboardData({
          orcamento: userData.ORCADO || 0,
          realizado: userData.REALIZADO || 0,
          economia: userData.ECONOMIA || 0,
          risco: userData.RISCO || 0,
          performance: userData.EFICIENTE || 0,
          previsao: userData.PREVISAO || 0,
        });
      } else {
        console.warn("⚠️ Nenhum dado encontrado para o contexto:", {
          contextYear,
          contextFilial,
        });
        setDashboardData({
          orcamento: 0,
          realizado: 0,
          economia: 0,
          risco: 0,
          performance: 0,
          previsao: 0,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dashboard:", error);
      setDashboardError(error.message);
      setDashboardData({
        orcamento: 0,
        realizado: 0,
        economia: 0,
        risco: 0,
        performance: 0,
        previsao: 0,
      });
    } finally {
      setDashboardLoading(false);
    }
  };

  // 👥 FUNÇÃO PARA BUSCAR DADOS DO DASHBOARD RH
  // Usa a mesma fonte de dados do RecursosHumanos (sql_MAN_ORC_RH) para calcular o Custo Total Orçado
  // Só busca se o usuário tiver permissão 'rh'
  const fetchDashboardDataRH = async (hasRHPermission = false) => {
    // Se não tem permissão de RH, não busca dados
    if (!hasRHPermission) {
      console.log("🔐 Usuário sem permissão de RH - não carrega dados de RH");
      setDashboardDataRH({ orcamento: 0, realizado: 0 });
      setDashboardRHLoading(false);
      return;
    }

    try {
      setDashboardRHLoading(true);

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Token não encontrado");
      }

      const contextYear =
        selectedYear ||
        localStorage.getItem("selectedYear") ||
        new Date().getFullYear().toString();
      const contextFilialChave =
        selectedFilial || localStorage.getItem("selectedFilial");

      if (!contextFilialChave || !contextFilialChave.includes("-")) {
        setDashboardDataRH({
          orcamento: 0,
          realizado: 0,
        });
        return;
      }

      const [contextColigada, contextFilial] = contextFilialChave.split("-");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ Buscar dados de RH usando sql_MAN_ORC_RH (mesma fonte do RecursosHumanos)
      const responseRH = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_RH/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: `AND CODCOLIGADA = '${contextColigada}' AND CODFILIAL = '${contextFilial}' AND ANO='${contextYear}'`,
          }),
        }
      );

      const resultRH = await responseRH.json();

      if (resultRH.success && resultRH.data && resultRH.data.length > 0) {
        // ✅ Calcular Custo Total Orçado (mesma lógica do RecursosHumanos - getDashboardData)
        const funcionarios = resultRH.data;
        
        const totalSalarios = funcionarios.reduce(
          (sum, emp) => sum + parseFloat(emp.SALARIO || 0) + parseFloat(emp.D13 || 0) + parseFloat(emp.FERIAS || 0) + parseFloat(emp.SALARIOVARIAVEL || 0),
          0
        );
        
        const totalEncargos = funcionarios.reduce(
          (sum, emp) => sum + 
            (parseFloat(emp.FGTS || 0) * 12) + 
            parseFloat(emp.FERIAS_FGTS || 0) + 
            parseFloat(emp.D13_FGTS || 0) + 
            (parseFloat(emp.PIS || 0) * 12) + 
            parseFloat(emp.FERIAS_PIS || 0) + 
            parseFloat(emp.D13_PIS || 0),
          0
        );
        
        const totalAbono = funcionarios.reduce(
          (sum, emp) => sum + parseFloat(emp.ABONO || 0),
          0
        );
        
        const totalIndenizacao = funcionarios.reduce(
          (sum, emp) => sum + parseFloat(emp.INDENIZACAO || 0),
          0
        );
        
        const custoTotalOrcado = totalSalarios + totalEncargos + totalAbono + totalIndenizacao;

        // ✅ Buscar realizado usando sql_MAN_ORC_0020
        const responseRealizado = await fetch(
          `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0020/filter`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              WHERE: `AND X.ANO = ${contextYear}
                      AND X.CODFILIAL = '${contextFilial}'
                      AND X.CODCOLIGADA = '${contextColigada}'`,
            }),
          }
        );

        const resultRealizado = await responseRealizado.json();
        let realizado = 0;
        
        if (resultRealizado.success && resultRealizado.data && resultRealizado.data.length > 0) {
          realizado = resultRealizado.data[0].REALIZAZADO || resultRealizado.data[0].REALIZADO || 0;
        }

        console.log("✅ Dashboard RH carregado - Orçado:", custoTotalOrcado, "Realizado:", realizado);
        setDashboardDataRH({
          orcamento: custoTotalOrcado,
          realizado: realizado,
        });
      } else {
        setDashboardDataRH({
          orcamento: 0,
          realizado: 0,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dashboard RH:", error);
      setDashboardDataRH({
        orcamento: 0,
        realizado: 0,
      });
    } finally {
      setDashboardRHLoading(false);
    }
  };

  // 🏢 FUNÇÃO PARA BUSCAR FILIAIS - CORRIGIDA
  const fetchAvailableFiliais = async () => {
    try {
      console.log("🏢 Buscando filiais disponíveis...");

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0003/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: `AND U.CODUSUARIO = '${currentUsername}'`,
            ORDER_BY: "O.ANO DESC, GFILIAL.NOMEFANTASIA",
          }),
        }
      );

      const result = await response.json();

      if (result.success && result.data && result.data.length > 0) {
        const allData = result.data.map((item) => ({
          CODFILIAL: item.CODFILIAL,
          NOME: item.NOMEFANTASIA || `Filial ${item.CODFILIAL}`,
          CODCOLIGADA: item.CODCOLIGADA,
          ANO: item.ANO.toString(),
          ANO_TRAVA: item.TRAVA.toString(),
          CHAVE_UNICA: `${item.CODCOLIGADA}-${item.CODFILIAL}`,
        }));

        setAvailableFiliais(allData);

        const years = [...new Set(allData.map((item) => item.ANO))].sort(
          (a, b) => b - a
        );
        setAvailableYears(years);

        const AnoTrava = [...new Set(allData.map((item) => item.ANO_TRAVA))].sort(
          (a, b) => b - a
        );
        
        localStorage.setItem("selectedTrava", AnoTrava);
        console.log("📊 Ano Trava:", AnoTrava);

        console.log("📊 Anos disponíveis:", years);
        console.log("🏢 Filiais disponíveis:", allData.length);

        // 🔧 CORREÇÃO CRÍTICA: Verificação de contexto existente

        

        
        const savedYear = localStorage.getItem("selectedYear");
        const savedFilial = localStorage.getItem("selectedFilial");

        console.log("🔍 Verificando contexto salvo:", {
          savedYear,
          savedFilial,
        });

        // Só define ano padrão se não houver um salvo OU se o salvo não estiver disponível
        if (!savedYear || !years.includes(savedYear)) {
          const defaultYear = years[0]; // Ano mais recente disponível
          console.log(
            "⚙️ Definindo ano padrão:",
            defaultYear,
            "(motivo: não salvo ou indisponível)"
          );
          setSelectedYear(defaultYear);
          localStorage.setItem("selectedYear", defaultYear);
        } else {
          console.log("✅ Mantendo ano salvo:", savedYear);
          setSelectedYear(savedYear); // Garante que o estado está sincronizado
        }

        // Só define filial padrão se não houver uma salva
        if (!savedFilial && allData.length > 0) {
          const firstFilial = allData[0].CHAVE_UNICA;
          console.log("⚙️ Definindo filial padrão:", firstFilial);
          setSelectedFilial(firstFilial);
          localStorage.setItem("selectedFilial", firstFilial);
        } else if (savedFilial) {
          console.log("✅ Mantendo filial salva:", savedFilial);
          setSelectedFilial(savedFilial); // Garante que o estado está sincronizado
        }
      }
    } catch (error) {
      console.error("❌ Erro ao buscar filiais e anos:", error);

      // Mesmo em caso de erro, tenta manter contexto do localStorage
      const savedYear = localStorage.getItem("selectedYear");
      if (savedYear) {
        console.log("⚠️ Erro na API, mas mantendo ano salvo:", savedYear);
        setSelectedYear(savedYear);
      } else {
        const currentYear = new Date().getFullYear().toString();
        console.log(
          "⚠️ Erro na API, definindo ano atual como padrão:",
          currentYear
        );
        setSelectedYear(currentYear);
        localStorage.setItem("selectedYear", currentYear);
      }
    }
  };

  // 🎯 FUNÇÃO PARA APLICAR CONTEXTO (APENAS VIA MODAL)
  const applyContext = async () => {
    console.log("🎯 Aplicando novo contexto:", {
      selectedYear,
      selectedFilial,
    });

    setContextLoading(true);

    // ⚠️ Aqui nascia o contexto pela metade: `selectedYear`/`selectedFilial`
    // eram gravados sempre, mas PCODCOLIGADA/PCODFILIAL só dentro de
    // `if (selectedFilialData)`. Com a lista de filiais ainda carregando, o
    // `find` voltava undefined, o cabeçalho passava a exibir "C1F10" e as telas
    // de lançamento consultavam com CODCOLIGADA = 'null'.
    // `selectedFilial` já é "COLIGADA-FILIAL": dá para derivar sem depender da
    // lista. Ela serve só para o nome amigável da filial.
    const selectedFilialData = availableFiliais.find(
      (f) => f.CHAVE_UNICA === selectedFilial
    );
    const [coligadaId, filialId] = String(selectedFilial || "").split("-");

    gravarContexto({
      coligada: selectedFilialData?.CODCOLIGADA ?? coligadaId,
      filial: selectedFilialData?.CODFILIAL ?? filialId,
      nomeFilial: selectedFilialData?.NOME,
      ano: selectedYear,
    });

    await fetchDashboardData();
    await fetchDashboardDataRH(getPermissionFromToken("rh"));
    setContextLoading(false);
    setShowContextModal(false);

    console.log("✅ Contexto aplicado com sucesso");
  };

  // 👤 FUNÇÃO PARA BUSCAR PERFIL DO USUÁRIO - VERSÃO CORRIGIDA
  const fetchUserProfile = async () => {
    console.log("👤 Carregando perfil do usuário...");
    setProfileLoading(true);

    const token = localStorage.getItem("authToken");
    if (!token) {
      setUserProfile((prev) => ({
        ...prev,
        nome: prev?.nome || "Usuário",
        codusuario: prev?.codusuario || "guest",
      }));
      setProfileLoading(false);
      return;
    }

    const controller = new AbortController();
    // ⚠️ 60s, e não 10s. Esta consulta traz a foto do usuário como array de
    // bytes em JSON (~800 KB de imagem => ~3 MB de resposta). De uma unidade
    // remota isso não fecha em 10s: o abort disparava, o `catch` não gravava
    // contexto nenhum, e as telas de lançamento passavam a consultar com
    // CODCOLIGADA = 'null'. É a origem do "codfilial nulo em alguns usuários".
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0002/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: `AND CODUSUARIO = '${currentUsername}'  `,
            ORDER_BY: "CODUSUARIO",
          }),
          signal: controller.signal,
        }
      );

      const result = await response.json();

      if (result.success && result.data && result.data.length > 0) {
        const userData = result.data[0];

        setUserProfile({
          nome: userData.USUARIO || currentUsername,
          email: userData.EMAIL || "",
          imagem: userData.IMAGEM || "",
          coligada: userData.COLIGADA || "",
          filial: userData.FILIAL || "",
          codusuario: userData.CODUSUARIO || currentUsername,
          CODCOLIGADA: userData.CODCOLIGADA || "",
          CODFILIAL: userData.CODFILIAL || "",
        });

        setuserName(userData.USUARIO || currentUsername);

        // 🏷️ O Sidebar precisa do nome de verdade (o token só traz o login) e
        // não vale disparar a mesma sql_MAN_ORC_0002 de novo só para isso.
        if (userData.USUARIO) {
          localStorage.setItem("perfilNome", userData.USUARIO);
        }

        // 🔧 CORREÇÃO CRÍTICA: Verificação mais rigorosa antes de sobrescrever contexto
        const existingYear = localStorage.getItem("selectedYear");
        const existingFilial = localStorage.getItem("selectedFilial");

        console.log("🔍 Contexto existente no perfil:", {
          existingYear,
          existingFilial,
        });
        console.log("📄 Dados do usuário:", {
          coligada: userData.CODCOLIGADA,
          filial: userData.CODFILIAL,
        });

        // Só define contexto do usuário se NÃO existir nenhum contexto salvo
        if (!existingYear && !existingFilial) {
          console.log("⚙️ Definindo contexto inicial baseado no usuário");

          const userFilialKey = `${userData.CODCOLIGADA}-${userData.CODFILIAL}`;
          setSelectedFilial(userFilialKey);
          localStorage.setItem("selectedFilial", userFilialKey);

          // Define ano atual apenas se não existir
          const currentYear = new Date().getFullYear().toString();
          setSelectedYear(currentYear);
          localStorage.setItem("selectedYear", currentYear);

          console.log("✅ Contexto inicial definido:", {
            currentYear,
            userFilialKey,
          });
        } else {
          console.log("✅ Mantendo contexto existente, não sobrescrevendo");
        }

        // Sempre atualiza os dados auxiliares (não sobrescreve contexto principal)
        const currentFilial =
          existingFilial || `${userData.CODCOLIGADA}-${userData.CODFILIAL}`;
        const [coligadaId, filialId] = currentFilial.split("-");

        gravarContexto({
          coligada: coligadaId,
          filial: filialId,
          nomeFilial: userData.FILIAL,
        });

        // PROCESSAMENTO SIMPLES DA IMAGEM
        if (userData.IMAGEM) {
          try {
            if (
              typeof userData.IMAGEM === "string" &&
              userData.IMAGEM.length > 50
            ) {
              setFinalImageSrc(`data:image/jpeg;base64,${userData.IMAGEM}`);
            } else if (typeof userData.IMAGEM === "object") {
              // Conversão simples para Buffer do Node.js
              const bytes = new Uint8Array(
                userData.IMAGEM.data || userData.IMAGEM
              );
              const blob = new Blob([bytes], { type: "image/jpeg" });
              setFinalImageSrc(URL.createObjectURL(blob));
            }
          } catch (e) {
            console.warn("Erro ao processar imagem:", e);
          }
        }
      } else {
        setUserProfile((prev) => ({
          ...prev,
          nome: currentUsername,
          codusuario: currentUsername,
        }));
      }
    } catch (error) {
      console.error("❌ Erro ao buscar perfil:", error);
    } finally {
      clearTimeout(timeoutId);
      setProfileLoading(false);
      console.log("✅ Perfil carregado");
    }
  };

  // 🛡️ FUSÍVEL ANTI-LOADING ETERNO
  useEffect(() => {
    if (!profileLoading) return;
    const timeout = setTimeout(() => {
      setProfileLoading(false);
    }, 12000);
    return () => clearTimeout(timeout);
  }, [profileLoading]);

  // 💀 SAI DO SKELETON quando TODAS as cargas iniciais terminam (uma troca só,
  // sem a tela crescer/encolher em etapas). Depois disso nunca volta ao skeleton.
  useEffect(() => {
    if (booted) return;
    if (
      !profileLoading &&
      !dashboardLoading &&
      !dashboardRHLoading &&
      !loading
    ) {
      setBooted(true);
    }
  }, [booted, profileLoading, dashboardLoading, dashboardRHLoading, loading]);

  // 🛡️ FUSÍVEL DO SKELETON: no máximo 10s de esqueleto, mesmo com API travada
  useEffect(() => {
    const timeout = setTimeout(() => setBooted(true), 10000);
    return () => clearTimeout(timeout);
  }, []);

  // 🔌 API ECONÔMICA
  const checkApiStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/economic/status`);
      const result = await response.json();
      if (result.success) {
        setApiStatus("online");
        return true;
      } else {
        setApiStatus("offline");
        return false;
      }
    } catch (error) {
      setApiStatus("offline");
      return false;
    }
  };

  const fetchEconomicData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const isApiOnline = await checkApiStatus();
      if (!isApiOnline) {
        throw new Error("API econômica não está disponível");
      }

      const endpoint = forceRefresh
        ? `${API_BASE_URL}/economic/refresh`
        : `${API_BASE_URL}/economic/data`;

      const method = forceRefresh ? "POST" : "GET";
      const response = await fetch(endpoint, {
        method: method,
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(
          `Erro HTTP: ${response.status} - ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success && result.data) {
        setEconomicData(result.data);
        setApiStatus("online");
      } else {
        throw new Error(result.message || "Dados econômicos não disponíveis");
      }
    } catch (error) {
      setError(error.message);
      setApiStatus("error");
      // 🧷 Mantém o último dado bom: se um refresh falha, a tela não encolhe
      setEconomicData((prev) => prev);
    } finally {
      setLoading(false);
    }
  };

  // 👨‍💼 COMPONENTE DE AVATAR SIMPLIFICADO
  const UserAvatar = () => {
    const [imageLoadError, setImageLoadError] = useState(false);

    useEffect(() => {
      if (userProfile.imagem) setImageLoadError(false);
    }, [userProfile.imagem]);

    if (finalImageSrc && !imageLoadError) {
      return (
        <img
          src={finalImageSrc}
          alt="Avatar do usuário"
          onError={() => {
            setImageLoadError(true);
            setImageError(true);
          }}
          className="rounded-circle shadow-sm"
          style={{
            width: "60px",
            height: "60px",
            objectFit: "cover",
            border: "2px solid rgba(255,255,255,0.8)",
          }}
        />
      );
    }

    if (userProfile.imagem && imageLoadError && avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt="Avatar convertido"
          className="rounded-circle shadow-sm"
          style={{
            width: "60px",
            height: "60px",
            objectFit: "cover",
            border: "2px solid rgba(255,255,255,0.8)",
          }}
        />
      );
    }

    return (
      <div
        className="rounded-circle shadow-sm d-flex align-items-center justify-content-center"
        style={{
          width: "48px",
          height: "48px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        👨‍💼
      </div>
    );
  };

  // 🔄 FUNÇÕES AUXILIARES

  // 📊 INICIALIZAÇÃO - CORRIGIDA PARA NÃO RECRIAR CONTEXTO
  useEffect(() => {
    const initializeData = async () => {
      console.log("🔄 Iniciando carregamento da aplicação");

      // 1. Primeiro: Inicializa contexto do localStorage
      if (!contextInitialized) {
        initializeContextFromStorage();
      }

      // 2. Segundo: Busca perfil (SEM sobrescrever contexto existente)
      await fetchUserProfile();

      // 3. Terceiro: Busca filiais (SEM redefinir contexto se já existe)
      await fetchAvailableFiliais();

      // 4. Quarto: Carrega dashboard com contexto definido
      await fetchDashboardData();
      await fetchDashboardDataRH(getPermissionFromToken("rh"));

      console.log("✅ Inicialização completa");
    };

    initializeData();
  }, []); // Sem dependências para executar apenas na montagem

  // 💰 API ECONÔMICA
  useEffect(() => {
    fetchEconomicData();
    const interval = setInterval(() => {
      fetchEconomicData();
      fetchDashboardData();
    }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 🔧 FUNÇÃO AUXILIAR PARA DEBUG
  const debugContext = () => {
    console.log("🐛 DEBUG - Estado atual do contexto:");
    console.log("selectedYear (state):", selectedYear);
    console.log("selectedFilial (state):", selectedFilial);
    console.log(
      "selectedYear (localStorage):",
      localStorage.getItem("selectedYear")
    );
    console.log(
      "selectedFilial (localStorage):",
      localStorage.getItem("selectedFilial")
    );
    console.log("PCODCOLIGADA:", localStorage.getItem("PCODCOLIGADA"));
    console.log("PCODFILIAL:", localStorage.getItem("PCODFILIAL"));
    console.log("Filiais disponíveis:", availableFiliais.length);
    console.log("Anos disponíveis:", availableYears);
  };

  // Para debug - pode ser chamado no console: window.debugContext()
  if (typeof window !== "undefined") {
    window.debugContext = debugContext;
  }

  // 🎨 FUNÇÕES UTILITÁRIAS

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Bom dia";
    if (hour < 18) return "Boa tarde";
    return "Boa noite";
  };

  const getTimeBasedEmoji = () => {
    const hour = currentTime.getHours();
    if (hour < 6) return "🌙";
    if (hour < 12) return "☀️";
    if (hour < 18) return "🌤️";
    return "🌆";
  };

  const getConnectionStatus = () => {
    if (loading)
      return { icon: "🔄", text: "Carregando", color: "text-primary" };
    if (apiStatus === "online" && economicData)
      return { icon: "🟢", text: "Conectado FGV", color: "text-success" };
    if (apiStatus === "offline")
      return { icon: "🔴", text: "API Offline", color: "text-danger" };
    if (error)
      return { icon: "⚠️", text: "Erro na Conexão", color: "text-warning" };
    return { icon: "⚪", text: "Verificando", color: "text-muted" };
  };

  // 📊 DADOS PARA RENDER (considera permissão de RH)
  const hasRHAccess = hasPermission("rh");
  const orcamentoTotal = dashboardData.orcamento + (hasRHAccess ? dashboardDataRH.orcamento : 0);
  const realizadoTotal = dashboardData.realizado + (hasRHAccess ? dashboardDataRH.realizado : 0);

  const kpis = [
    {
      id: "bank",
      title: "Orçamento Total",
      value: orcamentoTotal.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
      subtitle: `Aprovado ${selectedYear}`,
      icon: "🏦",
      color: "from-blue-500 to-blue-700",
      change: "+2.1%",
      trend: "up",
    },
    {
      id: "realized",
      title: "Realizado",
      value: realizadoTotal.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
      subtitle: "Até hoje",
      icon: "💎",
      color: "from-emerald-500 to-emerald-700",
      change: "+5.3%",
      trend: "up",
    },
    {
      id: "forecast",
      title: "Tendência de fechamento do Orç.",
      value: (() => {
        // Projeção linear: (Realizado Total / mês atual) * 12
        const mesAtual = new Date().getMonth() + 1; // 1-12
        const tendencia = (realizadoTotal / mesAtual) * 12;
        const percentage = orcamentoTotal > 0 
          ? ((tendencia / orcamentoTotal) * 100).toFixed(2)
          : 0;
        const colorClass = percentage > 100 ? "text-danger" : "text-success";
        return (
          <span>
            {tendencia.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}{" "}
            |<span className={colorClass}> {percentage}%</span>
          </span>
        );
      })(),
      subtitle: `Até dezembro ${selectedYear}`,
      icon: "🔮",
      color: "from-orange-500 to-orange-700",
      change: "-0.8%",
      trend: "down",
    },
  ];

  const insights = economicData?.insights || [];

  // 📊 Instituições que originaram os insights ("IBGE - IPCA" → "IBGE"), sem
  // repetir. Usado no rodapé do card de Insights.
  const insightSources = [
    ...new Set(
      insights
        .map((i) => (i.source || "").split(" - ")[0].trim())
        .filter(Boolean)
    ),
  ];

  // 🔴 Insights que pedem atenção
  const insightsPrioritarios = insights.filter(
    (i) => i.priority === "MÁXIMA" || i.priority === "ALTA"
  ).length;

  const connectionStatus = getConnectionStatus();

  // 💀 SKELETON DO 1º CARREGAMENTO
  if (!booted) {
    return <HomeSkeleton hasRHAccess={hasRHAccess} />;
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <div className="container-fluid p-3 p-md-4 home-enter">
        {/* Header Inteligente — 3 linhas: saudação | contexto + perfil | cards */}
        <div className="mb-4">
          {/* LINHA 1 — saudação */}
          <h1 className="h3 fw-bold mb-3">
            {getTimeBasedEmoji()} {getGreeting()}, {userName}!
          </h1>

          {/* LINHA 2 — faixa de contexto (compacta, à esquerda) + data,
              contexto e avatar como três blocos separados (à direita) */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div
              className="alert alert-info border-0 shadow-sm mb-0 home-contexto"
              style={{ width: "fit-content" }}
            >
              <div className="d-flex flex-wrap align-items-center gap-3">
                <div className="d-flex align-items-center">
                  <strong className="text-info me-2 fs-5">
                    📊 Visualizando:
                  </strong>
                </div>

                <div className="d-flex align-items-center">
                  <span className="fw-bold text-dark me-2 fs-5">📅 Ano:</span>
                  <span className="badge bg-primary px-3 py-2 fs-5">
                    {selectedYear}
                  </span>
                </div>

                {selectedFilial && availableFiliais.length > 0 && (
                  <div className="d-flex align-items-center">
                    <span className="fw-bold text-dark me-2 fs-5">
                      🏢 Filial:
                    </span>
                    <span className="badge bg-success px-3 py-2 fs-5">
                      {availableFiliais.find(
                        (f) => f.CHAVE_UNICA === selectedFilial
                      )?.NOME || `Filial ${selectedFilial}`}
                    </span>
                  </div>
                )}

                {!selectedFilial && (
                  <div className="d-flex align-items-center">
                    <span className="fw-bold text-dark me-2 fs-5">
                      🏢 Filial:
                    </span>
                    <span className="badge bg-warning text-dark px-3 py-2 fs-5">
                      Aguardando seleção
                    </span>
                  </div>
                )}

                {economicData && (
                  <div className="d-flex align-items-center">
                    <span className="fw-bold text-dark me-2 fs-6">
                      📈 Dados Econômicos:
                    </span>
                    <span className="badge bg-info text-dark px-3 py-2 fs-6">
                      {economicData.lastUpdate}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="text-center">
                <div className="small text-muted">Hoje</div>
                <div className="fw-bold">
                  {currentTime.toLocaleDateString("pt-BR")}
                </div>
              </div>

              {/* 🎛️ BOTÃO DE MODAL CONTEXTO */}
              <div className="text-center">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setShowContextModal(true)}
                  title="Configurar contexto (Filial e Ano)"
                >
                  <span style={{ fontSize: "1.2rem" }}>⚙️</span>
                  <div className="small mt-1">
                    <div className="text-muted">Contexto</div>
                    <div className="fw-bold text-primary">
                      {selectedYear}
                      {selectedFilial
                        ? (() => {
                            const [coligada, filial] =
                              selectedFilial.split("-");
                            return ` | C${coligada}F${filial}`;
                          })()
                        : " | --"}
                    </div>
                  </div>
                </button>
              </div>

              {/* 🎯 AVATAR DO USUÁRIO (clique abre o menu de perfil/permissões) */}
              <div className="position-relative">
                <button
                  type="button"
                  className="position-relative border-0 bg-transparent p-0"
                  style={{ cursor: "pointer", lineHeight: 0 }}
                  onClick={() => setShowProfileModal((v) => !v)}
                  title="Ver perfil e permissões"
                >
                  <UserAvatar />
                  {profileLoading && !finalImageSrc && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-100 rounded-circle">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  )}
                </button>

              {/* 👤 MENU DE PERFIL (dropdown ancorado no avatar) */}
              {showProfileModal && (
                <>
                  {/* camada invisível: clique fora fecha */}
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100"
                    style={{ zIndex: 1040 }}
                    onClick={() => setShowProfileModal(false)}
                  />
                  <div
                    className="position-absolute end-0 mt-2 bg-white rounded-3 shadow home-profile-menu"
                    style={{
                      top: "100%",
                      width: "320px",
                      maxWidth: "90vw",
                      zIndex: 1050,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    {/* Cabeçalho */}
                    <div className="d-flex align-items-center gap-3 p-3 border-bottom">
                      <UserAvatar />
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-bold text-truncate">
                          {userProfile.nome || userName}
                        </div>
                        {userProfile.email && (
                          <div className="text-muted small text-truncate">
                            {userProfile.email}
                          </div>
                        )}
                        <div className="text-muted small">
                          Usuário: {userProfile.codusuario || "--"}
                        </div>
                      </div>
                    </div>

                    {/* Permissões */}
                    <div className="p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-semibold mb-0 small">
                          🔐 Permissões de Acesso
                        </h6>
                        {(() => {
                          const ativas = Object.values(permissions).filter(
                            (v) => v === true
                          ).length;
                          return (
                            <span className="badge bg-primary bg-opacity-10 text-primary">
                              {ativas} {ativas === 1 ? "acesso" : "acessos"}
                            </span>
                          );
                        })()}
                      </div>

                      {(() => {
                        const permLabels = {
                          receita: "Rec. Educacional",
                          receitadiv: "Receitas Diversas",
                          despesa: "Despesas",
                          despesaGeral: "Despesas Gerais",
                          redutor: "Redutor / Intercompany / Repasse",
                          investimento: "Investimentos",
                          transferencia: "Transferência",
                          rh: "Recursos Humanos",
                          relatorio: "Relatórios",
                          menuprocon: "Procon",
                          menumaqrat: "Máquina de Rateio",
                          menunaoconf: "Não Conformidades",
                          menurepasse: "Repasse",
                          menumenueletivas: "Rec. Eletivas e Eventuais",
                          menupretacao: "Prestação de Contas",
                          menudash: "Dashboard",
                        };

                        // Exibe apenas as permissões liberadas: listar as
                        // negadas revelaria módulos que o usuário nem
                        // deveria saber que existem.
                        const liberadas = Object.entries(permissions).filter(
                          ([, value]) => value === true
                        );

                        if (liberadas.length === 0) {
                          return (
                            <p className="text-muted small mb-0">
                              Nenhuma permissão liberada para este usuário.
                            </p>
                          );
                        }

                        return (
                          <div
                            className="d-flex flex-wrap gap-2"
                            style={{
                              maxHeight: "220px",
                              overflowY: "auto",
                            }}
                          >
                            {liberadas.map(([key]) => (
                              <span
                                key={key}
                                className="d-inline-flex align-items-center gap-1 rounded-pill bg-success bg-opacity-10 text-success"
                                style={{
                                  border: "1px solid rgba(25,135,84,0.35)",
                                  padding: "0.35rem 0.6rem",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                                title={key}
                              >
                                ✓ {permLabels[key] || key}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                  </div>
                </>
              )}
              </div>
            </div>
          </div>

          {/* LINHA 3 — cards de orçamento */}
          <div className="row g-3">
            {/* 1. DESPESA + INVESTIMENTO */}
            <div className={hasRHAccess ? "col-md-4" : "col-md-6"}>
              <div className="p-3 bg-white rounded-3 shadow-sm border h-100">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <span className="me-1" style={{ fontSize: "1rem" }}>🏢</span>
                    <h6 className="mb-0 fw-bold" style={{ fontSize: "0.85rem" }}>Despesa + Investimento</h6>
                  </div>
                  <div className="text-end">
                    {(() => {
                      const percentConsumo = dashboardData.orcamento > 0
                        ? Math.round((dashboardData.realizado / dashboardData.orcamento) * 100)
                        : 0;
                      return (
                        <span className={`fw-bold ${percentConsumo <= 60 ? "text-success" : percentConsumo <= 80 ? "text-warning" : "text-danger"}`}>
                          {percentConsumo}%
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const percentConsumo = dashboardData.orcamento > 0
                    ? Math.round((dashboardData.realizado / dashboardData.orcamento) * 100)
                    : 0;
                  let progressColor = percentConsumo <= 60 ? "#28a745" : percentConsumo <= 80 ? "#ffc107" : "#dc3545";
                  let bgColor = percentConsumo <= 60 ? "#d4edda" : percentConsumo <= 80 ? "#fff3cd" : "#f8d7da";
                  return (
                    <div className="progress" style={{ height: "6px", backgroundColor: bgColor, borderRadius: "10px" }}>
                      <div className="progress-bar" role="progressbar"
                        style={{ width: `${Math.min(percentConsumo, 100)}%`, backgroundColor: progressColor, borderRadius: "10px", transition: "width 1s ease-in-out" }}
                        aria-valuenow={percentConsumo} aria-valuemin="0" aria-valuemax="100"
                      />
                    </div>
                  );
                })()}
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">Orçado: {dashboardData.orcamento.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</small>
                  <small className="text-muted">Realizado: {dashboardData.realizado.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</small>
                </div>
              </div>
            </div>

            {/* 2. RH - Só mostra se tiver permissão */}
            {hasRHAccess && (
            <div className="col-md-4">
              <div className="p-3 bg-white rounded-3 shadow-sm border h-100">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <span className="me-1" style={{ fontSize: "1rem" }}>👥</span>
                    <h6 className="mb-0 fw-bold" style={{ fontSize: "0.85rem" }}>Recursos Humanos</h6>
                  </div>
                  <div className="text-end">
                    {(() => {
                      const percentConsumo = dashboardDataRH.orcamento > 0
                        ? Math.round((dashboardDataRH.realizado / dashboardDataRH.orcamento) * 100)
                        : 0;
                      return (
                        <span className={`fw-bold ${percentConsumo <= 60 ? "text-success" : percentConsumo <= 80 ? "text-warning" : "text-danger"}`}>
                          {dashboardRHLoading ? "..." : `${percentConsumo}%`}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const percentConsumo = dashboardDataRH.orcamento > 0
                    ? Math.round((dashboardDataRH.realizado / dashboardDataRH.orcamento) * 100)
                    : 0;
                  let progressColor = percentConsumo <= 60 ? "#28a745" : percentConsumo <= 80 ? "#ffc107" : "#dc3545";
                  let bgColor = percentConsumo <= 60 ? "#d4edda" : percentConsumo <= 80 ? "#fff3cd" : "#f8d7da";
                  return (
                    <div className="progress" style={{ height: "6px", backgroundColor: bgColor, borderRadius: "10px" }}>
                      <div className="progress-bar" role="progressbar"
                        style={{ width: `${Math.min(percentConsumo, 100)}%`, backgroundColor: progressColor, borderRadius: "10px", transition: "width 1s ease-in-out" }}
                        aria-valuenow={percentConsumo} aria-valuemin="0" aria-valuemax="100"
                      />
                    </div>
                  );
                })()}
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">Orçado: {dashboardDataRH.orcamento.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</small>
                  <small className="text-muted">Realizado: {dashboardDataRH.realizado.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</small>
                </div>
              </div>
            </div>
            )}

            {/* 3. TOTAL CONSOLIDADO - Usa variáveis que consideram permissão de RH */}
            <div className={hasRHAccess ? "col-md-4" : "col-md-6"}>
              <div className="p-3 bg-white rounded-3 shadow-sm border h-100" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)" }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <span className="me-1" style={{ fontSize: "1rem" }}>💰</span>
                    <h6 className="mb-0 fw-bold" style={{ fontSize: "0.85rem" }}>Total Consolidado</h6>
                  </div>
                  <div className="text-end">
                    {(() => {
                      const percentConsumo = orcamentoTotal > 0
                        ? Math.round((realizadoTotal / orcamentoTotal) * 100)
                        : 0;
                      return (
                        <span className={`fw-bold ${percentConsumo <= 60 ? "text-success" : percentConsumo <= 80 ? "text-warning" : "text-danger"}`}>
                          {percentConsumo}%
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const percentConsumo = orcamentoTotal > 0
                    ? Math.round((realizadoTotal / orcamentoTotal) * 100)
                    : 0;
                  let progressColor = percentConsumo <= 60 ? "#28a745" : percentConsumo <= 80 ? "#ffc107" : "#dc3545";
                  let bgColor = percentConsumo <= 60 ? "#d4edda" : percentConsumo <= 80 ? "#fff3cd" : "#f8d7da";
                  return (
                    <div className="progress" style={{ height: "6px", backgroundColor: bgColor, borderRadius: "10px" }}>
                      <div className="progress-bar" role="progressbar"
                        style={{ width: `${Math.min(percentConsumo, 100)}%`, backgroundColor: progressColor, borderRadius: "10px", transition: "width 1s ease-in-out" }}
                        aria-valuenow={percentConsumo} aria-valuemin="0" aria-valuemax="100"
                      />
                    </div>
                  );
                })()}
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">Orçado: {orcamentoTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</small>
                  <small className="text-muted">Realizado: {realizadoTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })}</small>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <small className="text-danger d-block mt-1">⚠️ {error}</small>
          )}
        </div>

        <div className="row g-4 mb-4">
          {kpis.map((kpi, index) => (
            <div key={kpi.id} className="col-lg-4 col-md-6 col-12">
              <div
                className="card border-0 shadow-sm h-100"
                style={{
                  minHeight: "160px",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  setHoveredCard(kpi.id);
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 30px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  setHoveredCard(null);
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 10px rgba(0,0,0,0.08)";
                }}
              >
                <div className="card-body p-3 p-md-4 d-flex flex-column justify-content-between">
                  {/* Header do Card */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "50px",
                        height: "50px",
                        background:
                          hoveredCard === kpi.id
                            ? "linear-gradient(135deg, #6c5ce7, #a29bfe)"
                            : "linear-gradient(135deg, #74b9ff, #0984e3)",
                        fontSize: "24px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {kpi.icon}
                    </div>

                    {/* Badge de Mudança - Oculto */}
                  </div>

                  {/* Conteúdo Principal */}
                  <div className="flex-grow-1">
                    <h6
                      className="text-muted mb-2 fw-normal"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {kpi.title}
                    </h6>

                    <div
                      className="mb-2 d-flex align-items-center"
                      style={{ minHeight: "35px" }}
                    >
                      {dashboardLoading ? (
                        <div className="placeholder-glow w-100">
                          <div
                            className="placeholder col-8 bg-primary bg-opacity-25"
                            style={{ height: "28px", borderRadius: "8px" }}
                          ></div>
                        </div>
                      ) : (
                        <h4
                          className="fw-bold text-dark mb-0"
                          style={{ fontSize: "1.8rem", lineHeight: "1.2" }}
                        >
                          {kpi.value}
                        </h4>
                      )}
                    </div>

                    <small className="text-muted fw-medium">
                      {kpi.subtitle}
                    </small>

                    {dashboardError && (
                      <div className="mt-2">
                        <small className="text-danger">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Erro ao carregar
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                {/* Linha de Cor no Rodapé */}
                <div
                  className="card-footer border-0 p-0"
                  style={{
                    height: "4px",
                    background:
                      hoveredCard === kpi.id
                        ? "linear-gradient(90deg, #6c5ce7, #a29bfe)"
                        : "linear-gradient(90deg, #74b9ff, #0984e3)",
                    borderRadius: "0 0 8px 8px",
                    transition: "all 0.3s ease",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="row g-4 pb-4">
          {/* Insights IA */}
          <div className="col-lg-8">
            <div
              className={`card border-0 shadow-sm${
                economicData ? " home-panel" : " mb-4"
              }`}
            >
              <div className="card-header bg-transparent border-0 pt-4">
                {/* flex-wrap: sem ele, em tela estreita o bloco da direita não
                    cede e o título fica com uma palavra por linha. */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center">
                    <div
                      className="me-3 rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 home-painel-icone"
                      style={{
                        width: "40px",
                        height: "40px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      }}
                    >
                      🧠
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold home-painel-titulo">
                        Insights Econômicos Inteligentes
                      </h5>
                      <p className="text-muted small mb-0">
                        Powered by FGV IBRE • Análise Premium dos Indicadores
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {/* d-inline-flex + lh-1: o emoji tem métrica maior que o
                        texto e, no fluxo inline, empurrava o rótulo para baixo
                        dentro da pílula */}
                    <div
                      className={`badge d-inline-flex align-items-center gap-1 lh-1 py-2 ${connectionStatus.color.replace(
                        "text-",
                        "bg-"
                      )}`}
                    >
                      <span className="text-white">{connectionStatus.icon}</span>
                      <span className="text-white">{connectionStatus.text}</span>
                    </div>
                    <button
                      className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center lh-1"
                      style={{ width: "38px", height: "31px" }}
                      onClick={() => fetchEconomicData(true)}
                      disabled={loading}
                      title="Atualizar dados econômicos"
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        "🔄"
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div
                className="card-body d-flex flex-column"
                style={{
                  // 🧷 Altura mínima reservada: estados vazio/erro/carregando
                  // ocupam o mesmo espaço, então a página não "pula"
                  minHeight: "420px",
                  opacity: loading && economicData ? 0.55 : 1,
                  transition: "opacity 0.25s ease",
                }}
              >
                {loading && !economicData ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">
                        Carregando dados econômicos...
                      </span>
                    </div>
                    <p className="mt-3 text-muted">
                      Conectando com API do FGV IBRE...
                    </p>
                  </div>
                ) : error && !economicData ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i
                        className="fas fa-exclamation-triangle text-warning"
                        style={{ fontSize: "3rem" }}
                      ></i>
                    </div>
                    <h6 className="text-muted mb-2">
                      Dados Econômicos Indisponíveis
                    </h6>
                    <p className="text-muted small mb-3">{error}</p>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => fetchEconomicData()}
                    >
                      🔄 Tentar Novamente
                    </button>
                    <div className="mt-3">
                      <small className="text-muted">
                        Verifique se o servidor backend está rodando em{" "}
                        {API_BASE_URL}
                      </small>
                    </div>
                  </div>
                ) : insights.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i
                        className="fas fa-chart-line text-muted"
                        style={{ fontSize: "3rem" }}
                      ></i>
                    </div>
                    <h6 className="text-muted mb-2">
                      Aguardando Dados Econômicos
                    </h6>
                    <p className="text-muted small">
                      Conectado à API, mas nenhum insight disponível no momento.
                    </p>
                  </div>
                ) : (
                  <div className="row g-3 flex-grow-1 align-content-between">
                    {insights.map((insight, index) => (
                      <div key={index} className="col-12">
                        <div
                          className="border rounded-3 p-3 h-100"
                          style={{
                            background:
                              "linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.6) 100%)",
                            border: "1px solid rgba(0,0,0,0.08) !important",
                            transition: "all 0.3s ease",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateX(8px)";
                            e.currentTarget.style.background =
                              "linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.9) 100%)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 25px rgba(0,0,0,0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateX(0)";
                            e.currentTarget.style.background =
                              "linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.6) 100%)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <div className="d-flex align-items-start">
                            <div className="me-3 fs-4">{insight.icon}</div>
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="fw-bold text-dark fs-6">
                                  {insight.title}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  {insight.priority && (
                                    <span
                                      className={`badge ${
                                        insight.priority === "MÁXIMA"
                                          ? "bg-danger"
                                          : insight.priority === "ALTA"
                                          ? "bg-warning"
                                          : "bg-info"
                                      } bg-opacity-20`}
                                    >
                                      <span
                                        className={
                                          insight.priority === "MÁXIMA"
                                            ? "text-danger"
                                            : insight.priority === "ALTA"
                                            ? "text-warning"
                                            : "text-info"
                                        }
                                      >
                                        {insight.priority}
                                      </span>
                                    </span>
                                  )}
                                  <span
                                    className={`badge bg-opacity-20 ${insight.color?.replace(
                                      "text-",
                                      "bg-"
                                    )}`}
                                  >
                                    <span className={insight.color}>
                                      {insight.impact}
                                    </span>
                                  </span>
                                </div>
                              </div>

                              <p className="text-muted mb-2 fs-6 lh-sm">
                                {insight.description}
                              </p>

                              {insight.analysis && (
                                <div className="mb-2">
                                  <small className="text-primary fw-semibold">
                                    💡 {insight.analysis}
                                  </small>
                                </div>
                              )}

                              {insight.action && (
                                <div className="mb-2">
                                  <small className="text-dark">
                                    <strong>Ação:</strong> {insight.action}
                                  </small>
                                </div>
                              )}

                              <div className="d-flex justify-content-between align-items-center mt-2">
                                <small className="text-muted">
                                  📊 {insight.source}
                                </small>
                                {insight.benchmark && (
                                  <small className="text-muted fw-medium">
                                    🎯 {insight.benchmark}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Rodapé com Fonte - mesmo padrão do card de Índices, mas com
                    as instituições dos insights (o card ao lado já mostra a
                    fonte do agregado, não faz sentido repetir).
                    Sem mt-auto: a lista acima cresce (flex-grow) e distribui a
                    folga entre os insights, então o rodapé segue logo abaixo
                    sem deixar um vão único acima do separador. */}
                {economicData && (
                  <div className="pt-3 mt-3 border-top">
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <small
                        className="text-muted text-truncate"
                        style={{ minWidth: 0 }}
                        title={insightSources.join(" · ")}
                      >
                        <span className="fw-medium">Fontes:</span>{" "}
                        {insightSources.length > 0
                          ? insightSources.join(" · ")
                          : "FGV IBRE"}
                      </small>
                      <small className="text-muted text-nowrap">
                        <span className="fw-medium text-dark">
                          {insights.length}
                        </span>{" "}
                        {insights.length === 1 ? "insight" : "insights"}
                        {insightsPrioritarios > 0 && (
                          <>
                            {" · "}
                            <span className="fw-medium text-danger">
                              {insightsPrioritarios}
                            </span>{" "}
                            {insightsPrioritarios === 1
                              ? "prioritário"
                              : "prioritários"}
                          </>
                        )}
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar Direita - Índices Econômicos TOP */}
          <div className="col-lg-4">
            {/* Índices Econômicos Principais */}
            {economicData && (
              <div className="card border-0 shadow-sm home-panel">
                <div className="card-header bg-transparent border-0 pt-4">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center">
                      <div
                        className="me-3 rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 home-painel-icone"
                        style={{
                          width: "40px",
                          height: "40px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        }}
                      >
                        📊
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold home-painel-titulo">
                          Índices Econômicos (FGV)
                        </h5>
                        <p className="text-muted small mb-0">
                          Dados em tempo real • {economicData.lastUpdate}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-success d-inline-flex align-items-center gap-1 lh-1 py-2">
                        <span className="text-white">●</span>
                        <span className="text-white">LIVE</span>
                      </span>
                    </div>
                  </div>
                </div>
                {/* card-body sem pt-2: mesmo padding do card de Insights, para a
                    1ª linha dos dois painéis começar exatamente na mesma altura */}
                <div className="card-body d-flex flex-column">
                  {/* Indicadores Principais - Grade 2x2 */}
                  <div className="row g-3 mb-4">
                    <div className="col-6">
                      <div
                        className="text-center p-3 rounded-3 h-100"
                        style={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          minHeight: "90px",
                        }}
                      >
                        <div className="fs-5 mb-1">📈</div>
                        <div className="small opacity-90">IGP-M</div>
                        <div className="fw-bold fs-5">
                          {economicData.igpM?.value || "--"}%
                        </div>
                        <div className="small opacity-75">
                          {economicData.igpM?.period || ""}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div
                        className="text-center p-3 rounded-3 h-100"
                        style={{
                          background:
                            "linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)",
                          color: "white",
                          minHeight: "90px",
                        }}
                      >
                        <div className="fs-5 mb-1">🏗️</div>
                        <div className="small opacity-90">INCC-M</div>
                        <div className="fw-bold fs-5">
                          {economicData.inccM?.value || "--"}%
                        </div>
                        <div className="small opacity-75">
                          {economicData.inccM?.period || ""}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div
                        className="text-center p-3 rounded-3 h-100"
                        style={{
                          background:
                            "linear-gradient(135deg, #00b894 0%, #00a085 100%)",
                          color: "white",
                          minHeight: "90px",
                        }}
                      >
                        <div className="fs-5 mb-1">🏛️</div>
                        <div className="small opacity-90">PIB</div>
                        <div className="fw-bold fs-5">
                          {economicData.pib?.value || "--"}%
                        </div>
                        <div className="small opacity-75">
                          {economicData.pib?.period || ""}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div
                        className="text-center p-3 rounded-3 h-100"
                        style={{
                          background:
                            "linear-gradient(135deg, #fd79a8 0%, #e84393 100%)",
                          color: "white",
                          minHeight: "90px",
                        }}
                      >
                        <div className="fs-5 mb-1">📊</div>
                        <div className="small opacity-90">IPCA</div>
                        <div className="fw-bold fs-5">
                          {economicData.ipcaIbge?.value || "--"}%
                        </div>
                        <div className="small opacity-75">
                          {economicData.ipcaIbge?.period || ""}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* TODOS OS 16 Indicadores Secundários em Grid Expandido */}
                  <div className="border-top pt-3">
                    <h6 className="mb-3 fw-semibold text-muted">
                      Todos os Indicadores
                    </h6>
                    <div className="row g-2">
                      {[
                        {
                          key: "inpcIbge",
                          label: "INPC (IBGE)",
                          icon: "👥",
                          desc: "Índice Nacional de Preços ao Consumidor",
                        },
                        {
                          key: "ipca15Ibge",
                          label: "IPCA-15",
                          icon: "🔍",
                          desc: "IPCA Prévia - 15 dias",
                        },
                        {
                          key: "ipcaeIbge",
                          label: "IPCA-E",
                          icon: "📋",
                          desc: "IPCA Especial",
                        },
                        {
                          key: "igpDiFgv",
                          label: "IGP-DI",
                          icon: "🏪",
                          desc: "IGP Disponibilidade Interna",
                        },
                        {
                          key: "ipaDi",
                          label: "IPA-DI",
                          icon: "📦",
                          desc: "Índice Preços Atacado DI",
                        },
                        {
                          key: "ipcDi",
                          label: "IPC-DI",
                          icon: "🛒",
                          desc: "Índice Preços Consumidor DI",
                        },
                        {
                          key: "inccDi",
                          label: "INCC-DI",
                          icon: "🔨",
                          desc: "Custo Construção DI",
                        },
                        {
                          key: "ipaM",
                          label: "IPA-M",
                          icon: "📦",
                          desc: "Índice Preços Atacado M",
                        },
                        {
                          key: "ipcM",
                          label: "IPC-M",
                          icon: "🛒",
                          desc: "Índice Preços Consumidor M",
                        },
                        {
                          key: "igp10Fgv",
                          label: "IGP-10",
                          icon: "📊",
                          desc: "Índice Geral Preços 10",
                        },
                        {
                          key: "ipcFipe",
                          label: "IPC-FIPE",
                          icon: "🏙️",
                          desc: "IPC São Paulo FIPE",
                        },
                        {
                          key: "ibcBrBcb",
                          label: "IBC-Br",
                          icon: "⚡",
                          desc: "Atividade Econômica BCB",
                        },
                      ].map((indicator) => {
                        const data = economicData[indicator.key];
                        return (
                          <div key={indicator.key} className="col-12 mb-2">
                            <div
                              className="d-flex align-items-center justify-content-between p-2 bg-light rounded position-relative"
                              style={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#e3f2fd";
                                e.currentTarget.style.transform =
                                  "translateX(4px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f8f9fa";
                                e.currentTarget.style.transform =
                                  "translateX(0)";
                              }}
                              title={indicator.desc}
                            >
                              <div className="d-flex align-items-center">
                                <span
                                  className="me-2"
                                  style={{ fontSize: "0.9rem" }}
                                >
                                  {indicator.icon}
                                </span>
                                <div>
                                  <span className="fw-medium small">
                                    {indicator.label}
                                  </span>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {indicator.desc}
                                  </div>
                                </div>
                              </div>
                              <div className="text-end">
                                <span className="fw-bold text-dark small">
                                  {data?.value ? `${data.value}%` : "--"}
                                </span>
                                {data?.trend && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {data.trend === "up"
                                      ? "📈"
                                      : data.trend === "down"
                                      ? "📉"
                                      : "➡️"}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Resumo Estatístico */}
                  <div className="mt-4 pt-3 border-top">
                    <div className="row g-2">
                      <div className="col-4">
                        <div className="text-center p-2 bg-success bg-opacity-10 rounded">
                          <div className="small text-success fw-bold">
                            Em Alta
                          </div>
                          <div className="text-success fw-bold">
                            {
                              Object.keys(economicData).filter(
                                (key) =>
                                  economicData[key]?.value > 0 &&
                                  economicData[key]?.trend === "up"
                              ).length
                            }
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-center p-2 bg-danger bg-opacity-10 rounded">
                          <div className="small text-danger fw-bold">
                            Em Queda
                          </div>
                          <div className="text-danger fw-bold">
                            {
                              Object.keys(economicData).filter(
                                (key) =>
                                  economicData[key]?.value < 0 &&
                                  economicData[key]?.trend === "down"
                              ).length
                            }
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-center p-2 bg-warning bg-opacity-10 rounded">
                          <div className="small text-warning fw-bold">
                            Estáveis
                          </div>
                          <div className="text-warning fw-bold">
                            {
                              Object.keys(economicData).filter(
                                (key) => economicData[key]?.trend === "neutral"
                              ).length
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Rodapé com Fonte - mesmo espaçamento do card de Insights */}
                  <div className="pt-3 mt-3 border-top">
                    <div className="d-flex align-items-center justify-content-between">
                      <small className="text-muted">
                        <span className="fw-medium">Fonte:</span>{" "}
                        {economicData.source?.replace("https://", "") ||
                          "FGV IBRE"}
                      </small>
                      <div className="d-flex align-items-center">
                        <span className="text-success me-1">●</span>
                        <small className="text-muted">
                          {economicData.success ? "Dados reais" : "Cache"}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 🎛️ MODAL DE CONTEXTO */}
      {showContextModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <span className="me-2">⚙️</span>
                  Configuração de Contexto
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowContextModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {/* Seleção de Ano */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">
                      📅 Ano de Referência
                    </label>
                    <select
                      className="form-select"
                      value={selectedYear}
                      onChange={(e) => {
                        const newYear = e.target.value;
                        setSelectedYear(newYear);

                        const filialsInNewYear = availableFiliais.filter(
                          (f) => f.ANO === newYear
                        );
                        if (
                          !filialsInNewYear.find(
                            (f) => f.CHAVE_UNICA === selectedFilial
                          )
                        ) {
                          setSelectedFilial("");
                        }
                      }}
                    >
                      {availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    {availableYears.length === 0 && (
                      <small className="text-muted">
                        Carregando anos disponíveis...
                      </small>
                    )}
                  </div>

                  {/* Seleção de Filial */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">🏢 Filial</label>
                    <select
                      className="form-select"
                      value={selectedFilial}
                      onChange={(e) => setSelectedFilial(e.target.value)}
                      disabled={availableFiliais.length === 0}
                    >
                      <option value="">Selecione uma filial...</option>
                      {availableFiliais
                        .filter((filial) => filial.ANO === selectedYear)
                        .map((filial) => (
                          <option
                            key={`${filial.CODCOLIGADA}-${filial.CODFILIAL}-${filial.ANO}`}
                            value={filial.CHAVE_UNICA}
                          >
                            Col.{filial.CODCOLIGADA} - Fil.{filial.CODFILIAL} -{" "}
                            {filial.NOME}
                          </option>
                        ))}
                    </select>
                    {availableFiliais.filter((f) => f.ANO === selectedYear)
                      .length === 0 &&
                      selectedYear && (
                        <small className="text-warning">
                          ⚠️ Nenhuma filial disponível para o ano {selectedYear}
                        </small>
                      )}
                    {availableFiliais.length === 0 && (
                      <small className="text-muted">
                        Carregando filiais disponíveis...
                      </small>
                    )}
                  </div>

                  {/* Preview das Configurações */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h6 className="mb-2">📋 Resumo da Configuração:</h6>
                      <div className="row">
                        <div className="col-6">
                          <strong>Ano:</strong> {selectedYear}
                        </div>
                        <div className="col-6">
                          <strong>Filial:</strong>{" "}
                          {selectedFilial
                            ? (() => {
                                const selectedData = availableFiliais.find(
                                  (f) => f.CHAVE_UNICA === selectedFilial
                                );
                                return selectedData
                                  ? `${selectedData.CODCOLIGADA}-${selectedData.CODFILIAL}`
                                  : selectedFilial;
                              })()
                            : "Não selecionada"}
                        </div>
                      </div>
                      {selectedFilial && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Dados do orçamento serão filtrados para: <br />
                            <strong>
                              {availableFiliais.find(
                                (f) =>
                                  f.CHAVE_UNICA === selectedFilial &&
                                  f.ANO === selectedYear
                              )?.NOME || selectedFilial}{" "}
                              - {selectedYear}
                            </strong>
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowContextModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={applyContext}
                  disabled={!selectedFilial || !selectedYear || contextLoading}
                >
                  {contextLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <span className="me-2">✅</span>
                      Aplicar Contexto
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* 💀 Entrada do conteúdo após o skeleton: só opacidade, sem deslocar nada */
        @keyframes homeEnter {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .home-enter {
          animation: homeEnter 0.3s ease-out;
        }

        /* 👤 Abertura do menu de perfil ancorado no avatar */
        @keyframes profileMenuIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .home-profile-menu {
          transform-origin: top right;
          animation: profileMenuIn 0.15s ease-out;
        }

        @media (prefers-reduced-motion: reduce) {
          .home-enter,
          .home-profile-menu { animation: none; }
        }

        /* 📱 Celular: a faixa de contexto foi dimensionada para o desktop. Nos
           ~340px úteis de um telefone, o fs-5 dos badges empilha a faixa em
           quatro linhas altas. */
        @media (max-width: 575.98px) {
          .home-contexto {
            padding: 0.75rem;
          }

          .home-contexto .fs-5,
          .home-contexto .fs-6 {
            font-size: 0.95rem !important;
          }

          .home-contexto .badge {
            padding: 0.3rem 0.55rem !important;
          }

          /* O cabeçalho do painel tem ~222px de largura no celular. Com ícone
             de 40px + me-3, sobravam 166px e "Insights Econômicos
             Inteligentes" caía em uma palavra por linha. O !important é
             necessário: as medidas do ícone são style inline. */
          .home-painel-icone {
            width: 32px !important;
            height: 32px !important;
            margin-right: 0.5rem !important;
            font-size: 0.9rem;
          }

          .home-painel-titulo {
            font-size: 1rem;
          }

          .home-painel-titulo + p {
            font-size: 0.8rem;
          }
        }

        .card {
          backdrop-filter: blur(10px);
        }

        .btn {
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .btn:hover {
          transform: translateY(-2px);
        }

        /* :not(.spinner-border-sm) — sem isso esta regra também engordava os
           spinners pequenos (botão 🔄 e avatar) para 3rem, esticando o header */
        .spinner-border:not(.spinner-border-sm) {
          width: 3rem;
          height: 3rem;
        }

        .rounded-circle {
          transition: all 0.3s ease;
        }

        .rounded-circle:hover {
          transform: scale(1.05);
        }

        .modal-content {
          border-radius: 15px;
          border: none;
          box-shadow: 0 20px 60px rgba(0,0,0,0.1);
        }

        .modal-header {
          border-bottom: 1px solid rgba(0,0,0,0.1);
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 15px 15px 0 0;
        }

        .modal-footer {
          border-top: 1px solid rgba(0,0,0,0.1);
          background: #f8f9fa;
          border-radius: 0 0 15px 15px;
        }

        .btn-outline-secondary:hover {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #667eea;
          color: white;
        }

        .placeholder-glow .placeholder {
          animation: placeholder-glow 2s ease-in-out infinite alternate;
        }

        @keyframes placeholder-glow {
          50% {
            opacity: 0.2;
          }
        }
      `,
        }}
      />
    </div>
  );
};

export default HomePage;
